import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath, path } from '../../../routing/helpers/router';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import ViewContainer from '../../../layout/screenSpecifics/Modules/DocumentManager/ViewContainer';
import DocumentManagerSidebar from '../../../layout/screenSpecifics/Modules/DocumentManager/Sidebar/DocumentManagerSidebar';
import * as moduleAction from '../../../store/actions/moduleAction';

export default function ModuleDocumentManagerItemListScreen() {
  const { moduleBridgeHex, parentItemHex, page, filtersHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.moduleData,
  );
  const permissionsLevel = useSelector(
    (s) => s.module[moduleBridgeHex]?.userBridge?.permissions?.level,
  );
  const loading = useSelector((state) => state.currentPage?.options?.loading);
  const itemsPage = useSelector((state) => state.currentPage?.itemsPage);
  const itLevel1 = useSelector(
    (state) => state.currentPage?.elementLevels?.level1,
  );
  const itLevel2 = useSelector(
    (state) => state.currentPage?.elementLevels?.level2,
  );
  const [items, setItems] = useState();
  const reloadModuleData = useSelector(
    (state) => state.currentPage?.reloadModuleData,
  );

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  useEffect(() => {
    if (permissionsLevel === 0 || filtersHex !== '0') {
      dispatch(currentPageAction.change('options', { loading: true }));
    } else {
      dispatch(currentPageAction.change('itemsPage', undefined, true));
      setItems(undefined);
    }
  }, [filtersHex, parentItemHex, permissionsLevel]);

  // > LOAD ITEM
  const loadItems = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_documentManager_item_list'), {
          moduleBridgeHex,
          parentItemHex,
          filtersHex,
          view: 'list',
          isAdmin: permissionsLevel === 0,
        })
        .then(({ data }) => {
          if (isMounted.current) {
            setItems(data);
            dispatch(currentPageAction.change('pagination', data.pagination));
            dispatch(
              currentPageAction.change('itemsPage', data.elementIDs, true),
            );
            dispatch(
              currentPageAction.change('breadcrumb', data.breadcrumb, true),
            );
            dispatch(currentPageAction.change('folders', data.folders, true));
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, parentItemHex, filtersHex, page, permissionsLevel],
  );

  useEffect(() => {
    if (loading && permissionsLevel !== undefined) {
      loadItems();
    }
  }, [loading, permissionsLevel]);
  // < LOAD ITEM

  // > LOAD ITEM LEVELS
  const loadItemLevel = useCallback(
    async (level) =>
      axios.post(axiosPath('api_r_user_module_documentManager_item_level'), {
        moduleBridgeHex,
        elementIDs: items?.elementIDs?.[page - 1],
        level,
        isFiltered: filtersHex !== '0',
      }),
    [moduleBridgeHex, items, filtersHex, page],
  );

  // Level 1
  useEffect(() => {
    if (items?.elementIDs) {
      if (items?.pagination?.last === 0) {
        dispatch(currentPageAction.change('options', { loading: false }));
        return;
      }

      if (items?.elementIDs?.[page - 1]) {
        window.scrollTo(0, 0);
        if (items?.elementIDs.length > 0) {
          loadItemLevel(1).then((itLvl1) => {
            dispatch(
              currentPageAction.change(
                'elementLevels',
                {
                  level1: !itLevel1
                    ? itLvl1?.data
                    : { ...itLevel1, ...itLvl1?.data },
                },
                true,
              ),
            );
            dispatch(currentPageAction.change('options', { loading: false }));
            loadItemLevel(2).then((itLvl2) => {
              dispatch(
                currentPageAction.change('elementLevels', {
                  level2: !itLevel2
                    ? itLvl2?.data
                    : { ...itLevel2, ...itLvl2?.data },
                }),
              );
            });
          });
        } else {
          dispatch(currentPageAction.change('options', { loading: false }));
        }
      } else {
        navigate(
          path({
            name: 'user_module_documentManager_item_list',
            params: {
              moduleBridgeHex,
              parentItemHex: itLevel1?.hex,
              page: 1,
              filtersHex,
            },
          }),
        );
      }
    }
  }, [items, page]);
  // < LOAD ITEM LEVELS

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    dispatch(
      moduleAction.fetch(
        axiosPath('api_r_user_module_documentManager_getModuleData'),
        'moduleData',
        {
          moduleBridgeHex,
        },
      ),
    );
  }, []);

  useEffect(() => {
    loadModuleData();
  }, [reloadModuleData]);
  // > LOAD MODULE DATA //

  // < STORE CALLS
  const loadModulePermissions = useCallback(async () => {
    try {
      await dispatch(
        moduleAction.fetch(
          axiosPath('api_r_user_module_documentManager_getPermissions'),
          'permissions',
          {
            moduleBridgeHex,
            subType: 'permissions',
          },
        ),
      );
      await dispatch(
        moduleAction.fetch(
          axiosPath('api_r_user_module_documentManager_getPermissions'),
          'userBridge',
          {
            moduleBridgeHex,
            subType: 'userBridge',
          },
        ),
      );
    } catch (err) {}
  }, [moduleBridgeHex]);

  useEffect(() => {
    loadModulePermissions();
  }, [moduleBridgeHex]);
  // < STORE CALLS

  return (
    <>
      <DocumentManagerSidebar activeRoute="user_module_documentManager_item_list" />
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `Repositorio ${
                    permissionsLevel === 0 ? '(ADMINISTRADOR)' : ''
                  }`,
                },
              ]
            : [{ name: '...' }]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          <ViewContainer view="list" items={itemsPage?.[page - 1]} />
        </div>
      </main>
    </>
  );
}
